import { inject, Injectable } from '@angular/core';
import { FooterButtonType } from '../interfaces';
import { Message, MessageKind } from '@clientwire/api';
import { ActiveConversationStore, MessageStore } from '../stores';
import { SharedUtils } from '../utils';
import { throwError } from 'rxjs';
import { HttpClient, HttpHeaders, HttpResponse } from '@angular/common/http';
import { ActivityService } from './activity.service';
import { ConversationHelperService } from './conversation-helper.service';
import { TranslateService } from '@ngx-translate/core';
import { logger } from '../utils';
import { FilesToUpload } from '@akostm/orbit-ui';
import imageCompression from 'browser-image-compression';

@Injectable({
  providedIn: 'root',
})
export class MessageService {
  readonly #http = inject(HttpClient);
  readonly #conversationMessageStore = inject(MessageStore);
  readonly #conversationHelperService = inject(ConversationHelperService);
  readonly #activeConversationStore = inject(ActiveConversationStore);
  readonly #activityService = inject(ActivityService);
  readonly #allowedFileTypes: MessageKind[] = ['IMAGE', 'VIDEO', 'AUDIO'];
  readonly #translateService = inject(TranslateService);

  async sendMessage(event: FooterButtonType, conversationId: string) {
    switch (event) {
      case FooterButtonType.Text: {
        const message =
          this.#activeConversationStore.entityMap()[conversationId]?.messageInput.value;
        const messageSourceId =
          this.#activeConversationStore.entityMap()[conversationId]?.messageSourceId;
        const files = this.#activeConversationStore.entityMap()[conversationId]?.files;
        if (files && files.length > 0) {
          files.forEach((file) => {
            const fileType = file.file?.type?.split('/')[0]?.toUpperCase();
            if (fileType && this.#allowedFileTypes.includes(fileType as MessageKind)) {
              this.#conversationHelperService
                .createNewMessage(
                  conversationId,
                  fileType as MessageKind,
                  messageSourceId,
                  message ?? '',
                  undefined,
                  undefined,
                  file.src
                )
                .then((res: Message | null | undefined) => {
                  if (res) {
                    const response = res as Message;
                    if (response.asset?.uploadUrl) {
                      this.onUpload(conversationId, response.asset.uploadUrl, files, response);
                    }
                    this.setLastUnreadMessage(conversationId);
                  }
                });
              this.#activeConversationStore.setMessageInputValue(conversationId, '');
              this.#activeConversationStore.setNewMessageSourceId(conversationId);
              this.#activeConversationStore.updateConversation(conversationId, {
                files: [],
              });
            } else {
              this.#activeConversationStore.updateConversation(conversationId, {
                messageSending: false,
              });
            }
          });
        } else {
          if (message) {
            this.#conversationHelperService.createNewMessage(
              conversationId,
              'TEXT',
              messageSourceId,
              message
            );
          }
          this.#activeConversationStore.setMessageInputValue(conversationId, '');
          this.#activeConversationStore.setNewMessageSourceId(conversationId);
        }
        break;
      }

      default: {
        logger.debug(event);
        break;
      }
    }
  }

  async onUpload(
    conversationId: string,
    url: string,
    files: FilesToUpload[] | string[],
    message: Message
  ) {
    if (!files || files.length === 0) {
      return;
    }

    const file = this.#extractFileFromArray(files);
    if (!file) {
      throw new Error(this.#translateService.instant('SHARED.SERVICES.MESSAGE.FILE_NOT_FOUND'));
    }

    const processedFile = await this.#processFile(file, message);
    const headers = this.#createUploadHeaders(processedFile);

    this.#uploadFile(url, processedFile, headers, conversationId, message);
  }

  #extractFileFromArray(files: FilesToUpload[] | string[]): Blob | undefined {
    const tmpfile = files[0];
    if (!tmpfile) {
      return undefined;
    }

    if (typeof tmpfile === 'string') {
      return SharedUtils.dataURIToBlob(tmpfile);
    } else {
      return SharedUtils.dataURIToBlob(tmpfile.src);
    }
  }

  async #processFile(file: Blob, message: Message): Promise<Blob> {
    if (message.kind === 'IMAGE' && file instanceof Blob) {
      return await this.#compressImageWithFallback(file);
    }
    return file;
  }

  async #compressImageWithFallback(file: Blob): Promise<Blob> {
    try {
      return await this.compressImage(file);
    } catch (error) {
      logger.error('Image compression failed, using original file:', error);
      return file;
    }
  }

  #createUploadHeaders(processedFile: Blob): HttpHeaders {
    let headers = new HttpHeaders();
    if (processedFile instanceof Blob && processedFile.type) {
      headers = headers.set('Content-Type', processedFile.type);
    }
    return headers;
  }

  #uploadFile(
    url: string,
    processedFile: Blob,
    headers: HttpHeaders,
    conversationId: string,
    message: Message
  ): void {
    const upload$ = this.#http.put(url, processedFile, { headers, observe: 'response' });
    upload$.subscribe({
      next: (res: HttpResponse<unknown>) => {
        this.#handleUploadSuccess(res, conversationId);
      },
      error: (error: unknown) => {
        this.#handleUploadError(error, conversationId, message);
      },
    });
  }

  #handleUploadSuccess(res: HttpResponse<unknown>, conversationId: string): void {
    if (res.status >= 200 && res.status < 300) {
      this.#activeConversationStore.updateConversation(conversationId, {
        messageSending: false,
      });
    }
  }

  #handleUploadError(error: unknown, conversationId: string, message: Message): void {
    this.#conversationMessageStore.addMessageIdToErrors(message);
    this.#activeConversationStore.updateConversation(conversationId, {
      messageSending: false,
    });
    throwError(() => error);
  }

  /**
   * Compresses an image using browser-image-compression
   * @param file - The image file to compress
   * @returns Promise<Blob> - The compressed image as a Blob
   */
  private async compressImage(file: Blob): Promise<Blob> {
    try {
      const options = {
        maxSizeMB: 1, // Maximum file size in MB
        maxWidthOrHeight: 1024, // Maximum width or height
        useWebWorker: true, // Use web worker for better performance

        initialQuality: 0.8, // Initial quality (0.1 - 1.0)
      };

      const compressedFile = await imageCompression(file as File, options);
      return compressedFile;
    } catch (error) {
      logger.error('Browser image compression error:', error);
      throw error;
    }
  }

  async setLastUnreadMessage(conversationId: string, message?: Message) {
    let latestMessage = message;
    if (!latestMessage) {
      const conversation = this.#conversationMessageStore.entityMap()[conversationId];
      latestMessage = conversation?.messages[0];
    }

    if (latestMessage) {
      await this.#activityService.handleMessageSeenActivity(latestMessage);
    }
  }

  async setUnreadMessage(message: Message) {
    await this.#activityService.handleMessageSeenActivity(message);
  }

  resendMessage(conversationId: string, messageId: string) {
    console.log('resendMessage', conversationId, messageId);
    const message = this.#conversationMessageStore
      .entityMap()
      [conversationId]?.messages?.find((message) => message.id === messageId);
    if (message) {
      // Check if the message id is a local message or a server message
      if (messageId.startsWith('new-')) {
        // remove messageid from messageIdsWithError
        this.#conversationMessageStore.removeMessageIdFromErrors(conversationId, messageId);
        // Send again to the server
        this.#conversationMessageStore.sendLocalMessageToServer(message);
      } else {
        // The message already exists on the server probably an assets upload error
        // Try re-upload the assets
        // remove messageid from messageIdsWithError
        this.#conversationMessageStore.removeMessageIdFromErrors(conversationId, messageId);
        // Send again to the server
        const uploadUrl = message.asset?.uploadUrl;
        const file = message.asset?.downloadUrl;
        if (file && uploadUrl) {
          this.onUpload(conversationId, uploadUrl, [file], message);
        }
      }
    }
  }
}
